import React, { useState } from 'react';
import { useAccountingReport } from './useAccountingReport';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';
import DetalleCuentaModal from './DetalleCuentaModal';
import ExportButton from '../common/ExportButton';
import { exportToCSV, exportToExcel, exportToPDF } from '../../utils/exportUtils';

function BalanzaComprobacion({ month, year }) {
    const { userData } = useAuth();

    // The start and end of the selected month
    const startOfMonth = React.useMemo(() =>
        moment().year(year).month(month).startOf('month').toDate(),
        [year, month]
    );

    const endDate = React.useMemo(() =>
        moment().year(year).month(month).endOf('month').toDate(),
        [year, month]
    );

    // Start date: Beginning of time for full historical tracking
    const startDate = React.useMemo(() => new Date('2020-01-01'), []);

    const { loading, accounts, ledgerEntries } = useAccountingReport(
        userData?.condominioId,
        startDate,
        endDate,
        true
    );

    // Modal State
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedEntries, setSelectedEntries] = useState([]);

    const handleAccountClick = (account) => {
        const relevantMovements = [];
        ledgerEntries.forEach(asiento => {
            if (asiento.detalles) {
                asiento.detalles.forEach(detalle => {
                    if (detalle.cuentaId === account.id) {
                        relevantMovements.push({
                            ...detalle,
                            fecha: asiento.fecha,
                            concepto: asiento.concepto,
                            tipoReferencia: asiento.tipoReferencia,
                            referenciaId: asiento.referenciaId
                        });
                    }
                });
            }
        });
        setSelectedAccount(account);
        setSelectedEntries(relevantMovements);
    };

    // Calculate dynamic rows with Initial, Monthly Debits/Credits, and Ending Balances
    const rows = React.useMemo(() => {
        if (!accounts || accounts.length === 0) return [];

        const accountMap = {};
        accounts.forEach(acc => {
            accountMap[acc.id] = {
                ...acc,
                initialBalance: 0,
                debeMes: 0,
                haberMes: 0,
                finalBalance: 0
            };
        });

        ledgerEntries.forEach(entry => {
            let date = new Date();
            const rawDate = entry.fecha || entry.date || entry.createdAt || entry.creadoEn;
            if (rawDate && typeof rawDate.toDate === 'function') {
                date = rawDate.toDate();
            } else if (rawDate) {
                date = new Date(rawDate);
            }
            if (isNaN(date.getTime())) date = new Date();

            const isHistorical = date < startOfMonth;

            const detailsList = entry.detalles || entry.details || entry.entries || [];
            detailsList.forEach(detail => {
                const accId = detail.cuentaId || detail.accountId || detail.accountCode;
                if (accId && accountMap[accId]) {
                    const debe = parseFloat(detail.debe || (detail.amount && detail.type === 'debit' ? detail.amount : 0));
                    const haber = parseFloat(detail.haber || (detail.amount && detail.type === 'credit' ? detail.amount : 0));
                    const netChange = debe - haber;

                    if (isHistorical) {
                        accountMap[accId].initialBalance += netChange;
                    } else {
                        accountMap[accId].debeMes += debe;
                        accountMap[accId].haberMes += haber;
                    }
                }
            });
        });

        // Compute final balances and filter out rows with no balance and no activity
        return Object.values(accountMap).map(acc => {
            acc.finalBalance = acc.initialBalance + (acc.debeMes - acc.haberMes);

            const hasActivity = acc.debeMes > 0 || acc.haberMes > 0;
            const hasBalance = Math.abs(acc.initialBalance) >= 0.01 || Math.abs(acc.finalBalance) >= 0.01;

            if (!hasActivity && !hasBalance) return null;

            return acc;
        }).filter(Boolean).sort((a, b) => a.numero.localeCompare(b.numero));
    }, [accounts, ledgerEntries, startOfMonth]);

    // Calculate totals for sum validation
    const totals = React.useMemo(() => {
        let totalInitDebe = 0;
        let totalInitHaber = 0;
        let totalDebeM = 0;
        let totalHaberM = 0;
        let totalFinDebe = 0;
        let totalFinHaber = 0;

        rows.forEach(r => {
            // Initial Balance
            if (r.initialBalance > 0) totalInitDebe += r.initialBalance;
            else totalInitHaber += Math.abs(r.initialBalance);

            // Monthly Movements
            totalDebeM += r.debeMes;
            totalHaberM += r.haberMes;

            // Final Balance
            if (r.finalBalance > 0) totalFinDebe += r.finalBalance;
            else totalFinHaber += Math.abs(r.finalBalance);
        });

        return {
            totalInitDebe,
            totalInitHaber,
            totalDebeM,
            totalHaberM,
            totalFinDebe,
            totalFinHaber,
            isBalanced: Math.abs(totalDebeM - totalHaberM) < 1.0 && 
                        Math.abs(totalInitDebe - totalInitHaber) < 1.0 &&
                        Math.abs(totalFinDebe - totalFinHaber) < 1.0
        };
    }, [rows]);

    const formatCurrency = (val) => {
        if (Math.abs(val) < 0.01) return '-';
        return `$${val.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatBalanceCell = (val) => {
        if (Math.abs(val) < 0.01) return '-';
        if (val > 0) {
            return `${formatCurrency(val)} D`;
        } else {
            return `${formatCurrency(Math.abs(val))} A`;
        }
    };

    const getExportData = () => {
        const data = rows.map(row => ({
            Codigo: row.numero,
            Cuenta: row.nombre,
            'Saldo Inicial': row.initialBalance > 0 ? `${row.initialBalance.toFixed(2)} D` : `${Math.abs(row.initialBalance).toFixed(2)} A`,
            'Debe (Mes)': row.debeMes,
            'Haber (Mes)': row.haberMes,
            'Saldo Final': row.finalBalance > 0 ? `${row.finalBalance.toFixed(2)} D` : `${Math.abs(row.finalBalance).toFixed(2)} A`
        }));
        
        // Add totals
        data.push({
            Codigo: '',
            Cuenta: 'TOTALES DEBE',
            'Saldo Inicial': `${totals.totalInitDebe.toFixed(2)} D`,
            'Debe (Mes)': totals.totalDebeM,
            'Haber (Mes)': totals.totalHaberM,
            'Saldo Final': `${totals.totalFinDebe.toFixed(2)} D`
        });
        data.push({
            Codigo: '',
            Cuenta: 'TOTALES HABER',
            'Saldo Inicial': `${totals.totalInitHaber.toFixed(2)} A`,
            'Debe (Mes)': 0,
            'Haber (Mes)': 0,
            'Saldo Final': `${totals.totalFinHaber.toFixed(2)} A`
        });
        return data;
    };

    const handleExportCSV = () => {
        const data = getExportData();
        exportToCSV(data, `Balanza_Comprobacion_${month + 1}_${year}`);
    };

    const handleExportExcel = () => {
        const data = getExportData();
        exportToExcel(data, `Balanza_Comprobacion_${month + 1}_${year}`, 'Balanza', userData?.condominioNombre);
    };

    const handleExportPDF = () => {
        const columns = ['Código', 'Cuenta', 'Saldo Inicial', 'Debe (Mes)', 'Haber (Mes)', 'Saldo Final'];
        const data = rows.map(row => [
            row.numero,
            row.nombre,
            row.initialBalance > 0 ? `${row.initialBalance.toLocaleString('es-MX', { minimumFractionDigits: 2 })} D` : `${Math.abs(row.initialBalance).toLocaleString('es-MX', { minimumFractionDigits: 2 })} A`,
            row.debeMes > 0 ? `$${row.debeMes.toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '-',
            row.haberMes > 0 ? `$${row.haberMes.toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '-',
            row.finalBalance > 0 ? `${row.finalBalance.toLocaleString('es-MX', { minimumFractionDigits: 2 })} D` : `${Math.abs(row.finalBalance).toLocaleString('es-MX', { minimumFractionDigits: 2 })} A`
        ]);

        // Totals rows
        data.push([
            '', 
            'TOTAL DEUDOR / DEBE', 
            `${totals.totalInitDebe.toLocaleString('es-MX', { minimumFractionDigits: 2 })} D`, 
            `$${totals.totalDebeM.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 
            `$${totals.totalHaberM.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 
            `${totals.totalFinDebe.toLocaleString('es-MX', { minimumFractionDigits: 2 })} D`
        ]);
        data.push([
            '', 
            'TOTAL ACREEDOR / HABER', 
            `${totals.totalInitHaber.toLocaleString('es-MX', { minimumFractionDigits: 2 })} A`, 
            '-', 
            '-', 
            `${totals.totalFinHaber.toLocaleString('es-MX', { minimumFractionDigits: 2 })} A`
        ]);

        exportToPDF(
            `Balanza de Comprobación - ${month + 1}/${year}`,
            columns,
            data,
            `Balanza_Comprobacion_${month + 1}_${year}`,
            userData?.condominioNombre
        );
    };

    if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>Cargando Balanza...</div>;

    return (
        <div className="report-container" style={{ position: 'relative' }}>
            {selectedAccount && (
                <DetalleCuentaModal
                    account={selectedAccount}
                    entries={selectedEntries}
                    onClose={() => setSelectedAccount(null)}
                />
            )}

            <div style={{ position: 'absolute', top: '-60px', right: '0' }}>
                <ExportButton
                    onExportCSV={handleExportCSV}
                    onExportExcel={handleExportExcel}
                    onExportPDF={handleExportPDF}
                />
            </div>

            {/* Balanza Balance Status */}
            {!totals.isBalanced && (
                <div style={{ padding: '15px', background: '#fee2e2', color: '#ef4444', borderRadius: '8px', marginBottom: '20px', fontWeight: 'bold', textAlign: 'center', border: '1px solid #fecaca' }}>
                    ⚠️ LA BALANZA ESTÁ DESCUADRADA
                    <br />
                    <span style={{ fontSize: '0.9em', fontWeight: 'normal' }}>Verifique la cuadratura de pólizas en el Libro Diario.</span>
                </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                        <th style={{ padding: '12px', color: '#6b7280', fontWeight: '600' }}>Código</th>
                        <th style={{ padding: '12px', color: '#6b7280', fontWeight: '600' }}>Cuenta</th>
                        <th style={{ padding: '12px', color: '#6b7280', fontWeight: '600', textAlign: 'right' }}>Saldo Inicial</th>
                        <th style={{ padding: '12px', color: '#6b7280', fontWeight: '600', textAlign: 'right' }}>Debe (Mes)</th>
                        <th style={{ padding: '12px', color: '#6b7280', fontWeight: '600', textAlign: 'right' }}>Haber (Mes)</th>
                        <th style={{ padding: '12px', color: '#6b7280', fontWeight: '600', textAlign: 'right' }}>Saldo Final</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <tr
                            key={row.id}
                            style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', transition: 'background 0.2s' }}
                            onClick={() => handleAccountClick(row)}
                            className="report-row"
                        >
                            <td style={{ padding: '10px', color: '#6b7280', fontFamily: 'monospace' }}>{row.numero}</td>
                            <td style={{ padding: '10px', fontWeight: '500', color: '#111827' }}>{row.nombre}</td>
                            <td style={{ padding: '10px', textAlign: 'right', color: row.initialBalance ? '#111827' : '#d1d5db' }}>
                                {formatBalanceCell(row.initialBalance)}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'right', color: row.debeMes ? '#111827' : '#d1d5db' }}>
                                {row.debeMes ? formatCurrency(row.debeMes) : '-'}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'right', color: row.haberMes ? '#111827' : '#d1d5db' }}>
                                {row.haberMes ? formatCurrency(row.haberMes) : '-'}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'right', color: row.finalBalance ? '#111827' : '#d1d5db' }}>
                                {formatBalanceCell(row.finalBalance)}
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr style={{ background: '#f3f4f6', fontWeight: '700', borderTop: '2px solid #e5e7eb', fontSize: '0.9rem' }}>
                        <td colSpan="2" style={{ padding: '12px', verticalAlign: 'middle' }}>TOTALES</td>
                        
                        {/* Initial Balance Totals */}
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                            <div style={{ color: '#059669' }}>D: {formatCurrency(totals.totalInitDebe)}</div>
                            <div style={{ color: '#dc2626' }}>A: {formatCurrency(totals.totalInitHaber)}</div>
                        </td>
                        
                        {/* Month Debits */}
                        <td style={{ padding: '12px', textAlign: 'right', color: '#1e3a8a', verticalAlign: 'middle' }}>
                            {formatCurrency(totals.totalDebeM)}
                        </td>
                        
                        {/* Month Credits */}
                        <td style={{ padding: '12px', textAlign: 'right', color: '#1e3a8a', verticalAlign: 'middle' }}>
                            {formatCurrency(totals.totalHaberM)}
                        </td>
                        
                        {/* Final Balance Totals */}
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                            <div style={{ color: '#059669' }}>D: {formatCurrency(totals.totalFinDebe)}</div>
                            <div style={{ color: '#dc2626' }}>A: {formatCurrency(totals.totalFinHaber)}</div>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

export default BalanzaComprobacion;
